import { useCallback, useEffect, useState } from "react";
import { googleAuthService } from "../services/googleAuth";
import { userProfileService } from "../services/UserProfileService";
import { createLogger } from "../utils/logger";
import { useAuth } from "./useAuth";

const logger = createLogger("useOnboarding");

export interface OnboardingStep {
  id: string;
  title: string;
  completed: boolean;
  required: boolean;
}

export interface OnboardingState {
  isOnboardingNeeded: boolean;
  isOnboardingComplete: boolean;
  currentStep: number;
  nextIncompleteStep: number;
  steps: OnboardingStep[];
  isLoading: boolean;
  error: string | null;
}

const ONBOARDING_STEPS: Omit<OnboardingStep, "completed">[] = [
  {
    id: "user_info",
    title: "Tell Us About Yourself",
    required: true,
  },
  {
    id: "working_hours",
    title: "Set Working Hours",
    required: true,
  },
  {
    id: "google_connection",
    title: "Connect Google Services",
    required: true,
  },
  {
    id: "get_started",
    title: "Get Started",
    required: true,
  },
];

export const useOnboarding = () => {
  const { user } = useAuth();
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    isOnboardingNeeded: false,
    isOnboardingComplete: false,
    currentStep: 0,
    nextIncompleteStep: 0,
    steps: [],
    isLoading: true,
    error: null,
  });

  const checkOnboardingStatus = useCallback(async () => {
    if (!user) {
      setOnboardingState((prev) => ({
        ...prev,
        isLoading: false,
        isOnboardingNeeded: false,
        isOnboardingComplete: false,
      }));
      return;
    }

    // Don't make API calls for unverified users
    if (!user.emailVerified) {
      logger.debug("Skipping onboarding check for unverified user");
      setOnboardingState((prev) => ({
        ...prev,
        isLoading: false,
        isOnboardingNeeded: false,
        isOnboardingComplete: false,
      }));
      return;
    }

    try {
      setOnboardingState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Check if onboarding has been completed
      let onboardingCompleted = false;
      try {
        const userProfile = await userProfileService.getUserProfile();
        onboardingCompleted = userProfile?.onboardingCompleted === true;
      } catch (error) {
        // If we can't fetch profile or user doesn't exist, assume onboarding not completed
        onboardingCompleted = false;
      }

      // For display purposes, still check individual steps
      const googleStatus = await googleAuthService.getConnectionStatus();
      const isGoogleConnected = googleStatus.connected && googleStatus.hasRefreshToken;

      let hasUserInfo = false;
      let hasWorkingHours = false;
      try {
        const userProfile = await userProfileService.getUserProfile();
        hasUserInfo = !!(userProfile?.name && userProfile?.company && userProfile?.jobRole);
        hasWorkingHours = !!(userProfile?.timezone && userProfile?.workingHours && Object.values(userProfile.workingHours).some((day) => day.enabled));
      } catch (error) {
        hasUserInfo = false;
        hasWorkingHours = false;
      }

      const steps: OnboardingStep[] = ONBOARDING_STEPS.map((step) => ({
        ...step,
        completed:
          step.id === "user_info"
            ? hasUserInfo
            : step.id === "working_hours"
              ? hasWorkingHours
              : step.id === "google_connection"
                ? isGoogleConnected
                : step.id === "get_started"
                  ? onboardingCompleted
                  : false,
      }));

      const isOnboardingNeeded = !onboardingCompleted;
      const isOnboardingComplete = onboardingCompleted;
      const nextIncompleteStep = onboardingCompleted ? -1 : steps.findIndex((step) => step.required && !step.completed);

      setOnboardingState((prev) => ({
        isOnboardingNeeded,
        isOnboardingComplete,
        currentStep: prev.currentStep === 0 && prev.steps.length === 0 ? (nextIncompleteStep === -1 ? steps.length - 1 : nextIncompleteStep) : prev.currentStep, // Only set initial step, then preserve manual navigation
        nextIncompleteStep: nextIncompleteStep === -1 ? steps.length - 1 : nextIncompleteStep,
        steps,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      setOnboardingState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to check onboarding status",
      }));
    }
  }, [user]);

  // Add a method to refresh onboarding status (useful after OAuth completion)
  const refreshOnboardingStatus = useCallback(async () => {
    await checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const markStepCompleted = useCallback((stepId: string) => {
    setOnboardingState((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => (step.id === stepId ? { ...step, completed: true } : step)),
    }));
  }, []);

  const goToNextStep = useCallback(() => {
    setOnboardingState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, prev.steps.length - 1),
    }));
  }, []);

  const goToPreviousStep = useCallback(() => {
    setOnboardingState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
  }, []);

  const resetOnboarding = useCallback(async () => {
    setOnboardingState((prev) => ({
      ...prev,
      isOnboardingNeeded: true,
      isOnboardingComplete: false,
      currentStep: 0,
    }));
    // Refresh the onboarding status to get updated data from backend
    await checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const markOnboardingCompleted = useCallback(async () => {
    try {
      await userProfileService.updateUserProfile({ onboardingCompleted: true });
      // Directly update the state instead of calling checkOnboardingStatus
      setOnboardingState((prev) => ({
        ...prev,
        isOnboardingNeeded: false,
        isOnboardingComplete: true,
        steps: prev.steps.map((step) => (step.id === "get_started" ? { ...step, completed: true } : step)),
      }));
    } catch (error) {
      console.error("Error marking onboarding as completed:", error);
    }
  }, []);

  // Check onboarding status when user changes
  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  return {
    ...onboardingState,
    checkOnboardingStatus,
    refreshOnboardingStatus,
    markStepCompleted,
    goToNextStep,
    goToPreviousStep,
    resetOnboarding,
    markOnboardingCompleted,
  };
};
